import {
    ContentContractError
} from "./contentRepository.js";

import type {
    ContentCatalogEntry,
    ContentDocument,
    ContentRepository
} from "./contentRepository.js";

import type {
    HomeFeature,
    HomeFeatureKind,
    Level,
    NewsArticle,
    NewsIndexItem,
    TravelLesson,
    TravelLessonIndex,
    VocabPack,
    VocabPackIndex
} from "../../types/global.js";


/* -------------------------------------------------------------------------- */
/* Home features                                                              */
/* -------------------------------------------------------------------------- */

const homeFeatureKinds =
    new Set<HomeFeatureKind>([
        "news",
        "daily_phrase",
        "tip",
        "grammar",
        "vocabulary",
        "culture",
        "travel",
        "announcement",
        "other"
    ]);


/**
 * Loads only published Home editorial cards.
 *
 * There is intentionally no historical/static fallback here.
 *
 * Scheduling is inclusive:
 *
 * startsOn <= today <= endsOn
 */
async function loadPublishedHomeFeatures(
    repository:
        ContentRepository,

    now:
        Date = new Date()
): Promise<HomeFeature[]> {
    const entries =
        await repository.listCatalog(
            "home_feature"
        );

    const today =
        toLocalDateKey(
            now
        );

    return entries
        .map(
            entry =>
                mapHomeFeature(
                    entry
                )
        )
        .filter(
            feature =>
                isHomeFeatureVisible(
                    feature,
                    today
                )
        )
        .sort(
            (
                left,
                right
            ) =>
                left.position
                - right.position
                || left.id.localeCompare(
                    right.id
                )
        );
}


/* -------------------------------------------------------------------------- */
/* Home mapping                                                               */
/* -------------------------------------------------------------------------- */

function mapHomeFeature(
    entry:
        ContentCatalogEntry
): HomeFeature {
    requireCatalogIdentity(
        entry
    );

    const catalog =
        entry.catalog;

    const kind =
        requireHomeFeatureKind(
            catalog.kind,
            `${entry.contentKey}.kind`
        );

    const title =
        requireText(
            catalog.title,
            `${entry.contentKey}.title`
        );

    const label =
        requireText(
            catalog.label,
            `${entry.contentKey}.label`
        );

    const icon =
        requireText(
            catalog.icon,
            `${entry.contentKey}.icon`
        );

    const position =
        requireInteger(
            catalog.position,
            `${entry.contentKey}.position`,
            0,
            999
        );

    const featured =
        requireBoolean(
            catalog.featured,
            `${entry.contentKey}.featured`
        );

    const titleFa =
        optionalText(
            catalog.title_fa,
            `${entry.contentKey}.title_fa`
        );

    const labelFa =
        optionalText(
            catalog.label_fa,
            `${entry.contentKey}.label_fa`
        );

    const meta =
        optionalText(
            catalog.meta,
            `${entry.contentKey}.meta`
        );

    const metaFa =
        optionalText(
            catalog.meta_fa,
            `${entry.contentKey}.meta_fa`
        );

    const href =
        optionalText(
            catalog.href,
            `${entry.contentKey}.href`
        );

    const startsOn =
        optionalDate(
            catalog.startsOn,
            `${entry.contentKey}.startsOn`
        );

    const endsOn =
        optionalDate(
            catalog.endsOn,
            `${entry.contentKey}.endsOn`
        );


    if (
        href
        && !isValidHomeDestination(
            href
        )
    ) {
        throw new ContentContractError(
            `${entry.contentKey}.href must be an internal path or HTTPS URL`
        );
    }


    if (
        startsOn
        && endsOn
        && startsOn > endsOn
    ) {
        throw new ContentContractError(
            `${entry.contentKey} has a reversed display period`
        );
    }


    return {
        id:
            entry.contentKey,

        kind,

        title,

        ...(titleFa
            ? {
                title_fa:
                    titleFa
            }
            : {}),

        label,

        ...(labelFa
            ? {
                label_fa:
                    labelFa
            }
            : {}),

        ...(meta
            ? {
                meta
            }
            : {}),

        ...(metaFa
            ? {
                meta_fa:
                    metaFa
            }
            : {}),

        icon,

        ...(href
            ? {
                href
            }
            : {}),

        position,

        featured,

        ...(startsOn
            ? {
                startsOn
            }
            : {}),

        ...(endsOn
            ? {
                endsOn
            }
            : {})
    };
}


/* -------------------------------------------------------------------------- */
/* Home visibility                                                           */
/* -------------------------------------------------------------------------- */

function isHomeFeatureVisible(
    feature:
        HomeFeature,

    today:
        string
): boolean {
    if (
        feature.startsOn
        && today < feature.startsOn
    ) {
        return false;
    }

    if (
        feature.endsOn
        && today > feature.endsOn
    ) {
        return false;
    }

    return true;
}


/**
 * Scheduling uses the learner's browser-local calendar day rather than UTC.
 *
 * A "phrase du jour" therefore changes at local midnight.
 */
function toLocalDateKey(
    date:
        Date
): string {
    if (
        Number.isNaN(
            date.valueOf()
        )
    ) {
        throw new TypeError(
            "Invalid current date"
        );
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth()
            + 1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
}


/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                 */
/* -------------------------------------------------------------------------- */

async function loadPublishedVocabularyIndex(
    repository:
        ContentRepository,

    level:
        Level
): Promise<VocabPackIndex[]> {
    const entries =
        await repository.listCatalog(
            "vocabulary_pack",
            level
        );

    return entries.map(
        entry => {
            requireLevel(
                entry,
                level
            );

            requireCatalogIdentity(
                entry
            );

            requireText(
                entry.catalog.title,
                `${entry.contentKey}.title`
            );

            const wordCount =
                entry.catalog.words;

            if (
                !Number.isInteger(
                    wordCount
                )
                || Number(
                    wordCount
                ) < 0
            ) {
                throw new ContentContractError(
                    `${entry.contentKey}.words must be a non-negative integer`
                );
            }

            return entry.catalog as unknown as VocabPackIndex;
        }
    );
}


async function loadPublishedVocabularyPack(
    repository:
        ContentRepository,

    level:
        Level,

    packId:
        string
): Promise<VocabPack | null> {
    const content =
        await repository.loadDocument(
            "vocabulary_pack",
            packId,
            level
        );

    if (
        !content
    ) {
        return null;
    }

    requireDocumentLevel(
        content,
        level
    );

    requireDocumentIdentity(
        content
    );

    if (
        !Array.isArray(
            content.document.words
        )
    ) {
        throw new ContentContractError(
            `${packId}.words must be an array`
        );
    }

    return {
        ...content.document as unknown as VocabPack,

        level
    };
}


/* -------------------------------------------------------------------------- */
/* News                                                                       */
/* -------------------------------------------------------------------------- */

async function loadPublishedNewsIndex(
    repository:
        ContentRepository
): Promise<NewsIndexItem[]> {
    const entries =
        await repository.listCatalog(
            "news_article"
        );

    return entries.map(
        entry => {
            requireCatalogIdentity(
                entry
            );

            requireNewsMetadata(
                entry.catalog,
                entry.contentKey
            );

            return entry.catalog as unknown as NewsIndexItem;
        }
    );
}


async function loadPublishedNewsArticle(
    repository:
        ContentRepository,

    articleId:
        string
): Promise<NewsArticle | null> {
    const content =
        await repository.loadDocument(
            "news_article",
            articleId
        );

    if (
        !content
    ) {
        return null;
    }

    requireDocumentIdentity(
        content
    );

    requireNewsMetadata(
        content.document,
        articleId
    );

    const body =
        content.document.content;

    if (
        !body
        || typeof body
        !== "object"
        || Array.isArray(
            body
        )
    ) {
        throw new ContentContractError(
            `${articleId}.content must be an object`
        );
    }

    requireText(
        body.fullText,
        `${articleId}.content.fullText`
    );

    requireText(
        body.simpleText,
        `${articleId}.content.simpleText`
    );

    return content.document as unknown as NewsArticle;
}


/* -------------------------------------------------------------------------- */
/* Travel                                                                     */
/* -------------------------------------------------------------------------- */

async function loadPublishedTravelIndex(
    repository:
        ContentRepository
): Promise<TravelLessonIndex[]> {
    const entries =
        await repository.listCatalog(
            "travel_lesson"
        );

    return entries.map(
        entry => {
            requireCatalogIdentity(
                entry
            );

            requireText(
                entry.catalog.title,
                `${entry.contentKey}.title`
            );

            return entry.catalog as unknown as TravelLessonIndex;
        }
    );
}


async function loadPublishedTravelLesson(
    repository:
        ContentRepository,

    lessonId:
        string
): Promise<TravelLesson | null> {
    const content =
        await repository.loadDocument(
            "travel_lesson",
            lessonId
        );

    if (
        !content
    ) {
        return null;
    }

    requireDocumentIdentity(
        content
    );

    requireText(
        content.document.title,
        `${lessonId}.title`
    );

    return content.document as unknown as TravelLesson;
}


/* -------------------------------------------------------------------------- */
/* News validation                                                           */
/* -------------------------------------------------------------------------- */

function requireNewsMetadata(
    value:
        ContentCatalogEntry["catalog"],

    contentKey:
        string
): void {
    for (
        const field
        of [
            "title",
            "image",
            "level",
            "publishedDate"
        ] as const
    ) {
        requireText(
            value[field],
            `${contentKey}.${field}`
        );
    }
}


/* -------------------------------------------------------------------------- */
/* Generic validation                                                        */
/* -------------------------------------------------------------------------- */

function requireLevel(
    entry:
        ContentCatalogEntry,

    expected:
        Level
): void {
    if (
        entry.level
        !== expected
    ) {
        throw new ContentContractError(
            `${entry.contentKey} belongs to ${entry.level ?? "no level"}, not ${expected}`
        );
    }
}


function requireDocumentLevel(
    content:
        ContentDocument,

    expected:
        Level
): void {
    if (
        content.level
        !== expected
        || content.document.level
        !== expected
    ) {
        throw new ContentContractError(
            `${content.contentKey} belongs to another level`
        );
    }
}


function requireCatalogIdentity(
    entry:
        ContentCatalogEntry
): void {
    if (
        entry.catalog.id
        !== entry.contentKey
    ) {
        throw new ContentContractError(
            `${entry.contentKey}.id must match its catalog identity`
        );
    }
}


function requireDocumentIdentity(
    content:
        ContentDocument
): void {
    if (
        content.document.id
        !== content.contentKey
    ) {
        throw new ContentContractError(
            `${content.contentKey}.id must match its document identity`
        );
    }
}


function requireText(
    value:
        unknown,

    field:
        string
): string {
    if (
        typeof value
        !== "string"
        || !value.trim()
    ) {
        throw new ContentContractError(
            `${field} must be non-empty text`
        );
    }

    return value.trim();
}


function optionalText(
    value:
        unknown,

    field:
        string
): string | undefined {
    if (
        value === null
        || value === undefined
    ) {
        return undefined;
    }

    if (
        typeof value
        !== "string"
    ) {
        throw new ContentContractError(
            `${field} must be text`
        );
    }

    return (
        value.trim()
        || undefined
    );
}


function requireBoolean(
    value:
        unknown,

    field:
        string
): boolean {
    if (
        typeof value
        !== "boolean"
    ) {
        throw new ContentContractError(
            `${field} must be a boolean`
        );
    }

    return value;
}


function requireInteger(
    value:
        unknown,

    field:
        string,

    minimum:
        number,

    maximum:
        number
): number {
    if (
        typeof value
        !== "number"
        || !Number.isInteger(
            value
        )
        || value < minimum
        || value > maximum
    ) {
        throw new ContentContractError(
            `${field} must be an integer between ${minimum} and ${maximum}`
        );
    }

    return value;
}


/* -------------------------------------------------------------------------- */
/* Home-specific validation                                                  */
/* -------------------------------------------------------------------------- */

function requireHomeFeatureKind(
    value:
        unknown,

    field:
        string
): HomeFeatureKind {
    if (
        typeof value
        !== "string"
        || !homeFeatureKinds.has(
            value as HomeFeatureKind
        )
    ) {
        throw new ContentContractError(
            `${field} contains an unsupported Home feature kind`
        );
    }

    return value as HomeFeatureKind;
}


function optionalDate(
    value:
        unknown,

    field:
        string
): string | undefined {
    const text =
        optionalText(
            value,
            field
        );

    if (
        !text
    ) {
        return undefined;
    }

    if (
        !isIsoDate(
            text
        )
    ) {
        throw new ContentContractError(
            `${field} must use YYYY-MM-DD`
        );
    }

    return text;
}


function isIsoDate(
    value:
        string
): boolean {
    if (
        !/^\d{4}-\d{2}-\d{2}$/u.test(
            value
        )
    ) {
        return false;
    }

    const date =
        new Date(
            `${value}T00:00:00Z`
        );

    return (
        !Number.isNaN(
            date.valueOf()
        )
        && date
            .toISOString()
            .slice(
                0,
                10
            )
        === value
    );
}


function isValidHomeDestination(
    value:
        string
): boolean {
    if (
        value.startsWith(
            "/"
        )
    ) {
        return (
            !value.startsWith(
                "//"
            )
        );
    }

    try {
        return (
            new URL(
                value
            ).protocol
            === "https:"
        );
    } catch {
        return false;
    }
}


/* -------------------------------------------------------------------------- */
/* Exports                                                                    */
/* -------------------------------------------------------------------------- */

export {
    isHomeFeatureVisible,
    loadPublishedHomeFeatures,
    loadPublishedNewsArticle,
    loadPublishedNewsIndex,
    loadPublishedTravelIndex,
    loadPublishedTravelLesson,
    loadPublishedVocabularyIndex,
    loadPublishedVocabularyPack,
    toLocalDateKey
};